import { memo } from "react";
import { motion } from "framer-motion";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";

const DEFAULT_COLORS = [
  "#2563eb",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#84cc16",
  "#f97316",
];

const CHART_COMPONENTS = {
  pie: PieChart,
  bar: BarChart,
  line: LineChart,
  area: AreaChart,
};

function DefaultTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border bg-background p-3 shadow-lg">
      {payload.map((entry) => (
        <div key={entry.name} className="flex justify-between gap-4 text-sm">
          <span>{entry.name}</span>

          <span className="font-semibold">
            {Number(entry.value ?? 0).toLocaleString("en-IN")}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderSeries(type, config, dataset, colors) {
  switch (type) {
    case "pie":
      return (
        <Pie
          data={dataset}
          dataKey={config.dataKey}
          nameKey={config.nameKey}
          innerRadius={config.innerRadius}
          outerRadius={config.outerRadius}
          paddingAngle={config.paddingAngle ?? 3}
          label={config.label}
        >
          {dataset.map((_, index) => (
            <Cell key={index} fill={colors[index % colors.length]} />
          ))}
        </Pie>
      );

    case "bar":
      return config.bars.map((bar, index) => (
        <Bar
          key={bar.dataKey}
          dataKey={bar.dataKey}
          radius={[6, 6, 0, 0]}
          fill={colors[index % colors.length]}
        />
      ));

    case "line":
      return config.lines.map((line, index) => (
        <Line
          key={line.dataKey}
          dataKey={line.dataKey}
          type={line.type ?? "monotone"}
          stroke={colors[index % colors.length]}
          strokeWidth={3}
          dot={false}
        />
      ));

    case "area":
      return config.areas.map((area, index) => (
        <Area
          key={area.dataKey}
          dataKey={area.dataKey}
          type={area.type ?? "monotone"}
          stackId={area.stackId}
          fill={colors[index % colors.length]}
          stroke={colors[index % colors.length]}
        />
      ));

    default:
      return null;
  }
}

function ChartRenderer({ chart, values, results, chartData }) {
  const {
    title,
    description,

    type,

    data,

    colors = DEFAULT_COLORS,

    showLegend = true,
    showTooltip = true,
    showGrid = true,

    xAxis,
    yAxis,

    height = 350,
  } = chart;

  const dataset = chart.resultsKey
    ? chartData?.[chart.resultsKey]
    : typeof data === "function"
      ? data({
          values,
          results,
        })
      : data;

  const Chart = CHART_COMPONENTS[type];

  if (!Chart || !Array.isArray(dataset)) return null;

  return (
    <motion.div
      layout
      initial={{
        opacity: 0,
        y: 12,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>

          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>

        <CardContent className="overflow-x-auto">
          <div
            style={{
              width: `max(100%, ${chart.minWidth ?? 580}px)`,
            }}
          >
            <ResponsiveContainer width="100%" height={height}>
              <Chart
                data={dataset}
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 30,
                }}
              >
                {(type === "bar" || type === "line" || type === "area") &&
                  showGrid && <CartesianGrid strokeDasharray="3 3" />}

                {(type === "bar" || type === "line" || type === "area") &&
                  xAxis && (
                    <XAxis
                      {...xAxis}
                      dataKey={xAxis?.dataKey}
                      minTickGap={40}
                    />
                  )}

                {(type === "bar" || type === "line" || type === "area") &&
                  yAxis && (
                    <YAxis
                      width={70}
                      {...yAxis}
                      dataKey={yAxis?.dataKey}
                      tickFormatter={yAxis?.tickFormatter}
                    />
                  )}

                {showTooltip && <Tooltip content={<DefaultTooltip />} />}

                {showLegend && <Legend />}

                {renderSeries(type, chart, dataset, colors)}
              </Chart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default memo(ChartRenderer);
